import { FC, ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { routers } from "@/configs";
import { useDialog, useIsLoggedIn } from "@/hooks";
import { restoreSession } from "@/lib/session";

interface PrivateRouteProps {
  children: ReactNode;
}

/** Where a visitor was heading when sign-in got in the way; see `useReturnAfterLogin`. */
const RETURN_TO_KEY = "auth:return-to";

/**
 * A page that needs an account.
 *
 * A visitor who lands on one - a shared link to their bets, a bookmark to the
 * settings - is taken to the lobby with the sign-in form open, and the page
 * they wanted is remembered for after. It used to bounce them to the lobby
 * with no word about why, and without `replace`, so Back went straight into
 * the bounce again.
 */
const PrivateRoute: FC<PrivateRouteProps> = ({ children }) => {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const location = useLocation();
  const { openDialog } = useDialog();

  useEffect(() => {
    if (isLoggedIn) return;
    let active = true;
    // A guest's first render does not wait for the cookie restore; only turn away once it has answered.
    void restoreSession().then(() => {
      if (!active) return;
      sessionStorage.setItem(RETURN_TO_KEY, `${location.pathname}${location.search}`);
      navigate(routers.Home, { replace: true });
      openDialog("auth");
    });
    return () => {
      active = false;
    };
  }, [isLoggedIn, navigate, openDialog, location.pathname, location.search]);

  return isLoggedIn ? children : null;
};

/**
 * Brings a player back to the page a `PrivateRoute` turned them away from,
 * once they have signed in. Mounted somewhere that is on every page.
 */
export const useReturnAfterLogin = () => {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) return;
    const target = sessionStorage.getItem(RETURN_TO_KEY);
    if (!target) return;
    sessionStorage.removeItem(RETURN_TO_KEY);
    navigate(target, { replace: true });
  }, [isLoggedIn, navigate]);
};

export default PrivateRoute;
